// ============================================================
// TurnCard - the turn as it stands, under the row you act as (v8.322.0).
//
// An instrument, not a sentence: the armed weapon (a quick pick over the
// rack), its fire mode and the modifiers in play; the target with the
// map's range and cover; the odds; and the trigger with END TURN beside
// it. Every element is a door to a writer that already exists - the card
// owns no state and rolls nothing. What it says comes from
// `engine/turnIntent.ts`; what it does goes through the shell's handlers.
//
// It stands over the map's top-left corner (plan design-gaps L2; it hung
// under the actor's row of the retired order column); acting as nobody,
// there is none, and END TURN stays at the order strip's end.
// ============================================================
import { useEffect, useState } from 'react';
import type { Impact } from '../engine/impacts';
import { IMPACT_TTL_MS } from '../engine/impacts';
import clsx from '../utils/clsx';
import styles from '../styles/TurnCard.module.css';
import type { TurnIntent } from '../engine/turnIntent';

interface TurnCardWeapon {
  index: number;
  name: string;
  isMelee: boolean;
}

export interface TurnCardProps {
  intent: TurnIntent;
  weapons: TurnCardWeapon[];
  selectedSlot: number | null;
  onArm: (index: number) => void;
  onAttack: () => void;
  /** Null while the tracker is off - there is no turn to end. */
  onEndTurn: (() => void) | null;
  /** My pips are all spent: END TURN lights, never presses itself. */
  endSuggested?: boolean;
  /** What just happened to the target, shown as a strip for a moment (v8.326.0). */
  impact?: Impact | null;
  /** The ledger's remove, for chips the viewer owns; absent chips are facts. */
  onRemoveChip?: (id: string) => void;
  /** Over the map, the card is headed "ROUND n · NAME ACTS" (plan design-gaps, L2); under a row it needs none. */
  heading?: string | null;
}

type Chip = TurnIntent['chips'][number];

/** The quick pick over the rack: the armed weapon's name opens it, a row arms that slot. */
function WeaponPicker({
  armed,
  weapons,
  selectedSlot,
  onArm,
}: {
  armed: string | null | undefined;
  weapons: TurnCardWeapon[];
  selectedSlot: number | null;
  onArm: (index: number) => void;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className={styles.weaponWrap}>
      <button
        type="button"
        className={clsx(styles.weapon, !armed && styles.weaponNone)}
        data-testid="turn-card-weapon"
        aria-haspopup="listbox"
        aria-expanded={picking}
        onClick={() => setPicking((p) => !p)}
        title={armed ? 'Arm another weapon' : 'Arm a weapon (1-9 on the rack)'}
      >
        {armed ?? 'NO WEAPON'}
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {picking && (
        <div className={styles.pick} role="listbox" aria-label="Arm a weapon" data-testid="turn-card-pick">
          {weapons.length === 0 && <div className={styles.pickEmpty}>Nothing on the rack</div>}
          {weapons.map((w) => (
            <WeaponOption
              key={w.index}
              w={w}
              armed={w.index === selectedSlot}
              onPick={() => {
                onArm(w.index);
                setPicking(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WeaponOption({ w, armed, onPick }: { w: TurnCardWeapon; armed: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={armed}
      className={clsx(styles.pickRow, armed && styles.pickRowArmed)}
      data-testid={`turn-card-weapon-${w.index}`}
      onClick={onPick}
    >
      <span className={styles.pickKey}>{w.index < 9 ? w.index + 1 : ''}</span>
      <span className={styles.pickName}>{w.name}</span>
      <span className={styles.pickKind}>{w.isMelee ? 'melee' : 'ranged'}</span>
    </button>
  );
}

/** The ledger's own chips - stance, custom, quality - are the viewer's to remove; the rest are facts. */
const REMOVABLE = new Set(['stance', 'custom', 'quality']);

function IntentChip({ c, onRemove }: { c: Chip; onRemove?: (id: string) => void }) {
  const sign = c.value > 0 ? '+' : '';
  return (
    <span
      className={clsx(styles.chip, styles[`chip_${c.source}`] ?? styles.chipMode)}
      data-testid={`turn-card-chip-${c.id}`}
      title={`${c.label} ${sign}${c.value}`}
    >
      {c.label} {sign}
      {c.value}
      {onRemove && REMOVABLE.has(c.source) && (
        <button
          type="button"
          className={styles.chipRemove}
          aria-label={`Remove ${c.label}`}
          onClick={() => onRemove(c.id)}
        >
          ×
        </button>
      )}
    </span>
  );
}

function TargetLine({ intent }: { intent: TurnIntent }) {
  return (
    <div className={styles.line}>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      <span className={clsx(styles.target, !intent.targetName && styles.targetNone)} data-testid="turn-card-target">
        {intent.targetName ?? 'NO TARGET'}
      </span>
      {intent.range && (
        <span className={clsx(styles.chip, styles.chipRange)} data-testid="turn-card-range">
          {intent.range.metres} · {intent.range.band}
        </span>
      )}
      {intent.cover && (
        <span className={clsx(styles.chip, styles.chipCover)} data-testid="turn-card-cover">
          {intent.cover}
        </span>
      )}
    </div>
  );
}

/** Why the attack cannot go, or else the odds. */
function Outlook({ intent }: { intent: TurnIntent }) {
  if (intent.verdict) {
    return (
      <div className={styles.verdict} data-testid="turn-card-verdict">
        {intent.verdict}
      </div>
    );
  }
  return (
    intent.odds && (
      <div className={styles.odds} data-testid="turn-card-odds">
        <b>{intent.odds.hit}</b>
        <span>to hit</span>
        <b>{intent.odds.dmg}</b>
        <span>exp. dmg</span>
      </div>
    )
  );
}

/**
 * The impact strip lives as long as a marker's number does, then the card
 * is the turn again. Keyed by the moment, so a second hit shows again; a
 * second hit also re-runs the effect, whose cleanup clears the first timer.
 */
function useImpactStrip(impact: Impact | null) {
  const [stripFor, setStripFor] = useState<number | null>(null);
  useEffect(() => {
    if (!impact) return undefined;
    setStripFor(impact.at);
    const t = setTimeout(() => setStripFor(null), IMPACT_TTL_MS);
    return () => clearTimeout(t);
  }, [impact]);
  return stripFor === impact?.at ? impact : null;
}

function ImpactStrip({ strip }: { strip: Impact }) {
  return (
    <div
      className={clsx(styles.strip, styles[`strip_${strip.outcome}`])}
      data-testid="turn-card-strip"
      data-outcome={strip.outcome}
    >
      {strip.summary || (strip.damage > 0 ? `${strip.damage} wounds` : strip.outcome.toUpperCase())}
    </div>
  );
}

function TriggerButtons({
  intent,
  onAttack,
  onEndTurn,
  endSuggested,
}: Pick<TurnCardProps, 'intent' | 'onAttack' | 'onEndTurn'> & { endSuggested: boolean }) {
  return (
    <div className={styles.buttons}>
      <button
        type="button"
        className={styles.attack}
        data-testid="turn-card-attack"
        onClick={onAttack}
        disabled={!intent.canFire}
        title={intent.canFire ? 'Fire the chamber (Space)' : 'Arm a weapon first'}
      >
        {intent.trigger}
      </button>
      {onEndTurn && (
        <button
          type="button"
          className={clsx(styles.end, endSuggested && styles.endSuggested)}
          data-testid="turn-card-end"
          data-suggested={endSuggested || undefined}
          onClick={onEndTurn}
          title="End the turn (E)"
        >
          END TURN
        </button>
      )}
    </div>
  );
}

export default function TurnCard({
  intent,
  weapons,
  selectedSlot,
  onArm,
  onAttack,
  onEndTurn,
  endSuggested = false,
  impact = null,
  onRemoveChip,
  heading = null,
}: TurnCardProps) {
  const strip = useImpactStrip(impact);
  return (
    <div className={styles.card} data-testid="turn-card" data-verdict={intent.verdict ?? undefined}>
      {heading && (
        <div className={styles.heading} data-testid="turn-card-heading">
          {heading}
        </div>
      )}
      <div className={styles.line}>
        <WeaponPicker armed={intent.weapon} weapons={weapons} selectedSlot={selectedSlot} onArm={onArm} />
        {intent.mode && <span className={clsx(styles.chip, styles.chipMode)}>{intent.mode}</span>}
        {intent.chips.map((c) => (
          <IntentChip key={c.id} c={c} onRemove={onRemoveChip} />
        ))}
      </div>
      <TargetLine intent={intent} />
      <Outlook intent={intent} />
      {strip && <ImpactStrip strip={strip} />}
      <TriggerButtons intent={intent} onAttack={onAttack} onEndTurn={onEndTurn} endSuggested={endSuggested} />
    </div>
  );
}
